import { TARGET_CHANGED_CODE } from "@shared/connectors/targetFields";
import type { DataflowSummary } from "@shared/types/dataflows";
import { parseRunLeftOut, type RunLeftOut, type RunLeftOutReason } from "@shared/types/runs";

/**
 * #1673: the connections a schedule's Run now leaves out, and the sentences that say so: in the
 * dialog before the click (`scheduleLeftOut`, the server's rule read from the Connections list),
 * and after it in the success notice and the run's `RUN_CREATED` event (`leftOutSentence`, from
 * the server's `leftOut`, ids and closed reasons only).
 */

/** Why, after "was left out because". */
const REASON_WORDS: Record<Exclude<RunLeftOutReason, "missing">, string> = {
  disabled: "it is disabled",
  untested: "its target changed since its last connection test",
  not_ready: "it has not finished setup",
};

/** The well formed entries of a payload's `leftOut`, anything else dropped. */
export const parseLeftOut = parseRunLeftOut;

/**
 * One sentence per connection left out, "Alpha was left out because it is disabled." ("is left
 * out" before the click), and one for the deleted ones together. Empty when nothing was left out.
 * A name the reader cannot see reads "A connection".
 */
export function leftOutSentence(
  leftOut: readonly RunLeftOut[],
  nameOf: (dataflowId: string) => string | undefined,
  /** "before" in the dialog, before the click ("is left out"); "after" once started. */
  when: "before" | "after" = "after",
): string {
  const one = when === "before" ? "is" : "was";
  const many = when === "before" ? "are" : "were";
  const sentences: string[] = [];
  let missing = 0;
  for (const entry of leftOut) {
    if (entry.reason === "missing") {
      missing += 1;
      continue;
    }
    const name = nameOf(entry.dataflowId) ?? "A connection";
    sentences.push(`${name} ${one} left out because ${REASON_WORDS[entry.reason]}.`);
  }
  if (missing === 1) sentences.push(`A deleted connection ${one} left out.`);
  if (missing > 1) sentences.push(`${missing} deleted connections ${many} left out.`);
  return sentences.join(" ");
}

/**
 * The server's rule (`RunService.scheduleRunPlan`) on the Connections list: each member once, in
 * the schedule's order, with the first reason that holds. `runnable` counts the members that run.
 * While the list has not loaded (`connections` empty) nothing is judged.
 */
export function scheduleLeftOut(
  dataflowIds: readonly string[],
  connections: readonly Pick<DataflowSummary, "id" | "state" | "lastValidation">[],
): { leftOut: RunLeftOut[]; runnable: number; runnableIds: string[] } {
  if (connections.length === 0) return { leftOut: [], runnable: 0, runnableIds: [] };
  const byId = new Map(connections.map((row) => [row.id, row]));
  const leftOut: RunLeftOut[] = [];
  const runnableIds: string[] = [];
  for (const id of new Set(dataflowIds)) {
    const row = byId.get(id);
    let reason: RunLeftOutReason | null = null;
    if (!row) reason = "missing";
    else if (row.state === "disabled") reason = "disabled";
    else if (row.lastValidation?.errorCode === TARGET_CHANGED_CODE) reason = "untested";
    else if (row.state === "draft") reason = "not_ready";
    if (reason) leftOut.push({ dataflowId: id, reason });
    else runnableIds.push(id);
  }
  return { leftOut, runnable: runnableIds.length, runnableIds };
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** What to do about a connection left out for this reason, by name or by pronoun. */
const NEXT_STEP: Record<
  Exclude<RunLeftOutReason, "missing">,
  { named: (names: string) => string; one: string; many: string }
> = {
  disabled: { named: (names) => `enable ${names}`, one: "enable it", many: "enable them" },
  untested: { named: (names) => `test ${names}`, one: "test it", many: "test them" },
  not_ready: {
    named: (names) => `finish the setup of ${names}`,
    one: "finish its setup",
    many: "finish their setup",
  },
};

const NEXT_STEP_ORDER = ["disabled", "untested", "not_ready"] as const;

/**
 * #1673: what to do when none of a schedule's connections can run, by reason: enable a disabled
 * one, test one whose target changed, finish a Draft's setup. A deleted connection has no fix of
 * its own, so with only deleted ones the step is the server's own: edit the schedule's
 * connections. One reason and nothing deleted reads "Enable it"; otherwise each action names its
 * connections, so no pronoun can point at a deleted one.
 */
export function leftOutNextStep(
  leftOut: readonly RunLeftOut[],
  nameOf: (dataflowId: string) => string | undefined,
): string {
  const groups = NEXT_STEP_ORDER.map((reason) => ({
    reason,
    ids: leftOut.filter((entry) => entry.reason === reason).map((entry) => entry.dataflowId),
  })).filter((group) => group.ids.length > 0);
  if (groups.length === 0) return "Edit the schedule's connections.";
  const anyMissing = leftOut.some((entry) => entry.reason === "missing");
  const [only] = groups;
  const clauses =
    only && groups.length === 1 && !anyMissing
      ? [only.ids.length === 1 ? NEXT_STEP[only.reason].one : NEXT_STEP[only.reason].many]
      : groups.map((group) =>
          NEXT_STEP[group.reason].named(
            joinNames(group.ids.map((id) => nameOf(id) ?? "the connection")),
          ),
        );
  const sentence = `${clauses.join(", ")}, or edit the schedule's connections.`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * #1673: the closing sentence of a Run now that leaves some connections out: the one that runs by
 * name ("Beta runs."), or how many run.
 */
export function runnableSentence(
  runnableIds: readonly string[],
  nameOf: (dataflowId: string) => string | undefined,
): string {
  if (runnableIds.length === 1) {
    const name = nameOf(runnableIds[0] as string);
    return name ? `${name} runs.` : "The other connection runs.";
  }
  return `The other ${runnableIds.length} connections run.`;
}
